import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
//configs & styles
import { Colors, FontSize, BreakPoints } from 'src/configs/StyleConfig';
//components
import StatusMapping from 'src/common/StatusMapping';
import ReactPaginate from 'react-paginate';
import BreakLongTextToFit from 'src/common/BreakLongTextToFit';
import Loader from 'react-loader';
//functions
import DateFunctions from 'src/functions/DateFunctions';
import PriceFunctions from 'src/functions/PriceFunctions';
import GeneralFunctions from 'src/functions/GeneralFunctions';
//hooks
import { useWindowSize } from 'src/views/hooks/useWindowSize';

const baseColumnNames = {
  status: { label: 'ステータス', width: '10%', align: 'left' },
  support_date: { label: '対応日/契約日/完了日', width: '8%', align: 'left' },
  business_partner: { label: '提携先/店舗名', width: '9%', align: 'left' },
  contract_no: { label: '契約書No/営業所', width: '9%', align: 'left' },
  customer_name: { label: '氏名', width: '8%', align: 'left' },
  customer_address1: { label: '住所', width: '16%', align: 'left' },
  destination_postal_code: { label: '施工先〒/住所', width: '16%', align: 'left' },
  payment_method: { label: '支払方法/備考', width: '8%', align: 'left' },
  content: { label: '工事内容/工事代金', width: '8%', align: 'center' },
  is_exported: { label: 'csv出力', width: '8%', align: 'left' },
};
const keyPairs = {
  support_date: ['support_date', 'contract_date', 'construction_end_date'],
  business_partner: ['business_partner', 'construction_store_name'],
  contract_no: ['contract_no', 'sales_office_name'],
  customer_name: ['customer_kana', 'customer_name'],
  customer_address1: ['customer_postal_code', 'customer_address1'],
  destination_postal_code: ['destination_postal_code', 'destination_address'],
  payment_method: ['payment_method', 'remarks'],
  content: ['content', 'total'],
  is_exported: ['exported_at', 'exported_employee_name']
};

const ConstructionTable = (props) => {

  const { data, navigation, sortType, isDataProcessing } = props;
  const { width } = useWindowSize();
  const [columnNames, setColumnNames] = useState(baseColumnNames);
  const [currentItems, setCurrentItems] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [isTwoLine, setIsTwoLine] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const itemsPerPage = 100;
  const textLimit = 12;

  useEffect(() => {
    //windowのサイズに合わせてテーブルのカラムを調整（初期化）
    if (window.innerWidth < 790) {
      const updatedColumnNames = {
        support_date: { label: '対応日/契約日/完了日', width: '8%', align: 'left' },
        business_partner: { label: '提携先/店舗名', width: '9%', align: 'left' },
        contract_no: { label: '契約書No/営業所', width: '9%', align: 'left' },
        customer_name: { label: '氏名', width: '8%', align: 'left' },
        customer_address1: { label: '住所', width: '16%', align: 'left' },
        destination_postal_code: { label: '施工先〒/住所', width: '16%', align: 'left' },
        payment_method: { label: '支払方法/備考', width: '8%', align: 'left' },
        content: { label: '工事内容/工事代金', width: '8%', align: 'center' },
      }
      setColumnNames(updatedColumnNames);
      setIsTwoLine(true);
    } else {
      setColumnNames(baseColumnNames);
      setIsTwoLine(false);
    }
  
    const endOffset = itemOffset + itemsPerPage;
    setCurrentItems(data.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(data.length / itemsPerPage));
    setIsLoading(false);
  },  [itemsPerPage, itemOffset, data, sortType]);

  const changeTableColumn = () => {
    //windowのサイズに合わせてテーブルのカラムを調整（アップデート）
    if (width < 790) {
      const updatedColumnNames = {
        support_date: { label: '対応日/契約日/完了日', width: '8%', align: 'left' },
        business_partner: { label: '提携先/店舗名', width: '9%', align: 'left' },
        contract_no: { label: '契約書No/営業所', width: '9%', align: 'left' },
        customer_name: { label: '氏名', width: '8%', align: 'left' },
        customer_address1: { label: '住所', width: '16%', align: 'left' },
        destination_postal_code: { label: '施工先〒/住所', width: '16%', align: 'left' },
        payment_method: { label: '支払方法/備考', width: '8%', align: 'left' },
        content: { label: '工事内容/工事代金', width: '8%', align: 'center' },
      }
      setColumnNames(updatedColumnNames);
      setIsTwoLine(true);
    } else {
      setColumnNames(baseColumnNames);
      setIsTwoLine(false);
    }
  };
  useMemo(changeTableColumn, [width]);

  const TableContent = ({ data }) => {

    return (
      <>
        {
          isTwoLine ?
            <TrTop>
              <td colSpan="6"><StatusMapping oneLine={true} statusList={data['status']} /></td>
              <td colSpan="1" />
              <td colSpan="1">
                <div style={{ display: 'flex', justifyContent: 'center', border: `1px solid ${Colors.statusRed}`, color: Colors.statusRed, fontSize: 10 }}>
                  出力済み
                </div>
              </td>
            </TrTop>
            :
            null
        }
        <Tr onClick={() => navigation(data.id)}>
          {
            Object.keys(columnNames).map((columns) => {
              if (columns === 'status') {
                return (
                  <Td width={columnNames[columns].width} key={columns}>
                    <StatusMapping statusList={data[columns]} />
                  </Td>
                )
              } else if (columns === 'support_date') {
                const key = keyPairs[columns];
                return (
                  <Td width={columnNames[columns].width} key={columns}>
                    <MultiLine>
                      {DateFunctions.ISOStringDateToLocalDate(data[key[0]]) ? DateFunctions.ISOStringDateToLocalDate(data[key[0]]) : " "}
                    </MultiLine>
                    <MultiLine>
                      {DateFunctions.ISOStringDateToLocalDate(data[key[1]]) ? DateFunctions.ISOStringDateToLocalDate(data[key[1]]) : " "}
                    </MultiLine>
                    <MultiLine>
                      {DateFunctions.ISOStringDateToLocalDate(data[key[2]]) ? DateFunctions.ISOStringDateToLocalDate(data[key[2]]) : " "}
                    </MultiLine>
                  </Td>
                )
              } else if (columns === 'construction_employee_name1') {
                const key = keyPairs[columns];
                return (
                  <Td width={columnNames[columns].width} key={columns}>
                    <MultiLine>
                      {data[key[0]]}
                    </MultiLine>
                    <MultiLine style={{ fontSize: 10 }}>
                      {GeneralFunctions.trimLongText(data[key[1]], textLimit)}
                    </MultiLine>
                  </Td>
                )
              } else if (columns === 'customer_address1') {
                //住所は文字数が多くなるのでBreakLongTextToFitでテキストを区切って表示
                const key = keyPairs[columns];
                return (
                  <Td width={columnNames[columns].width} key={columns}>
                    <MultiLine>
                      {data[key[0]]}
                    </MultiLine>
                    <BreakLongTextToFit component={MultiLine} styles={{fontSize: 10}} text={data[key[1]] ? data[key[1]] : ''} maxLengthInOneLine={16} /> 
                  </Td>
                )
              } else if (columns === 'destination_postal_code') {
                //顧客と施工先の住所を比較後、異なる住所の場合のみ表示
                if (!data['is_customer_address_identical']) {
                  const key = keyPairs[columns];
                  return (
                    <Td width={columnNames[columns].width} key={columns}>
                      <MultiLine>
                        {data[key[0]]}
                      </MultiLine>
                      <BreakLongTextToFit component={MultiLine} styles={{fontSize: 10}} text={data[key[1]] ? data[key[1]] : ''}  maxLengthInOneLine={16} /> 
                    </Td>
                  )
                }
                return <Td width={columnNames[columns].width} key={columns} />
              }  else if (columns === 'content') {
                const key = keyPairs[columns];
                return (
                  <Td style={{ textAlign: 'right', paddingRight: 12 }} width={columnNames[columns].width} key={columns}>
                    <BreakLongTextToFit component={MultiLine} styles={{fontSize: 10, textAlign: 'left'}} text={data[key[0]] ? data[key[0]] : ''} maxLengthInOneLine={10} /> 
                    <MultiLine>
                      {PriceFunctions.intPriceToStringPrice(data[key[1]])}
                    </MultiLine>
                  </Td>
                )
              } else if (columns === 'payment_method') {
                const key = keyPairs[columns];
                return (
                  <Td width={columnNames[columns].width} key={columns}>
                    <MultiLine>
                      {data[key[0]]}
                    </MultiLine>
                    <MultiLine>
                      {GeneralFunctions.trimLongText(data[key[1]], 10)}
                    </MultiLine>
                  </Td>
                )
              } else if (columns === 'is_exported') {
                if (data['is_exported'] === 1) {
                  const key = keyPairs[columns];
                  return (
                    <Td width={columnNames[columns].width} key={columns}>
                      <CSVStatusMultiLine style={{ border: `1px solid ${Colors.statusRed}` }}>
                        出力済み
                      </CSVStatusMultiLine>
                      <CSVStatusMultiLine>
                        {DateFunctions.ISOStringDateToLocalDate(data[key[0]]) ? DateFunctions.ISOStringDateToLocalDate(data[key[0]]) : " "}
                      </CSVStatusMultiLine>
                      <CSVStatusMultiLine>
                      {data[key[1]] ? data[key[1]] : " "}
                      </CSVStatusMultiLine>
                    </Td>
                  )
                }
                return (
                  <Td width={columnNames[columns].width} key={columns} />
                )
              } else if (columns === 'customer_name') {
                const key = keyPairs[columns];
                return (
                  <Td width={columnNames[columns].width} key={columns}>
                    <MultiLine style={{fontSize: 8}}>
                      {data[key[0]]}
                    </MultiLine>
                    <MultiLine>
                      {data[key[1]]}
                    </MultiLine>
                  </Td>
                )
              } 
              const key = keyPairs[columns];
              return (
                <Td width={columnNames[columns].width} key={columns}>
                  <MultiLine>
                    {data[key[0]]}
                  </MultiLine>
                  <MultiLine>
                    {data[key[1]]}
                  </MultiLine>
                </Td>
              )
            })
          }
        </Tr>
      </>
    )
  }

  const handlePageClick = useCallback((event) => {
    const newOffset = (event.selected * itemsPerPage) % data.length;
    setItemOffset(newOffset);
  }, [data, itemsPerPage, setItemOffset]);

  return (
    <Container>
      <Table>
        <TitleContainer>
          <tr>
            {
              Object.keys(columnNames).map((cName) => <Th align={columnNames[cName].align} key={cName}>{columnNames[cName].label}</Th>)
            }
          </tr>
        </TitleContainer>
        <tbody>
        {
         isDataProcessing || isLoading ?
          null
        :
          currentItems &&
          currentItems.map((data, index) => (
            <TableContent data={data} key={index.toString()} />
          ))
        }
        </tbody>
      </Table>
      {
        isDataProcessing || isLoading ?
        <div style={{ height: 320, display: 'flex', alignItems: 'center', backgroundColor: 'black'}}>
          <Loader position="relative" />
        </div>
        :
        null
      }
      <ReactPaginate
        breakLabel="..."
        nextLabel="next >"
        onPageChange={handlePageClick}
        pageRangeDisplayed={2}
        pageCount={pageCount}
        previousLabel="< previous"
        renderOnZeroPageCount={null}
        containerClassName={"pagination"}
        pageClassName={'page-selection'}
        previousLinkClassName={"pagination__link"}
        nextLinkClassName={"pagination__link"}
        disabledClassName={"pagination__link--disabled"}
        activeClassName={"pagination__link--active"}
      />
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  margin: 32px 0 0 0;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: ${BreakPoints.lg}) {
    width: 95%;
  }
  @media screen and (max-width: ${BreakPoints.md}) {
    width: 92%;
  }
`
const Table = styled.table`
  max-width: ${BreakPoints.xxl};
  min-width: ${BreakPoints.md};
  width: 96%;
  border-collapse: collapse;
`
const TitleContainer = styled.thead`
  border: 1px solid #3f3f3f;
  border-radius: 6px;
  height: 48px;
  background-color: #3f3f3f;
`
const Th = styled.th`
  color: #ffffff;
  font-size: ${FontSize.md};
  font-weight: 600;
  text-align: ${props => props.align ? props.align : 'left'};
  width: ${props => props.width && `${props.width}`};
`
const Tr = styled.tr`
  min-height: 80px;
  border-bottom: 1px solid lightgray;
  font-size: ${FontSize.xs};
  cursor: pointer;
  background-color: #ffffff;
  :hover {
    background-color: ${Colors.boxHighlight};
  }
  :after {
    content: "";
    display: block;
  }
`
const TrTop = styled.tr`
  height: 24px;
  font-size: ${FontSize.xs};
  cursor: pointer;
  background-color: #ffffff;
  @media screen and (max-width: 790) {
    border-bottom: 1px solid lightgray;
  }
`
const Td = styled.td`
  list-style-type: none;
  color: ${Colors.textColor};
  font-weight: 500;
  text-align: ${props => props.align ? props.align : 'left'};
  width: ${props => props.width && `${props.width}`};
`
const MultiLine = styled.pre`
  font-family: Arial, Helvetica, sans-serif;
  font-weight: lighter;
`

const CSVStatusMultiLine = styled(MultiLine)`
  display: flex; 
  justify-content: center;
  color: ${Colors.statusRed}; 
  font-size: 10px;
`

ConstructionTable.propTypes = {
  data: PropTypes.array.isRequired,
  navigation: PropTypes.func.isRequired,
  sortType: PropTypes.number.isRequired,
  isDataProcessing: PropTypes.bool.isRequired
};
ConstructionTable.defaultProps = {
  data: [],
  navigation: () => { },
  sortType: 0,
  isDataProcessing: true
};


export default ConstructionTable;